import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// Add auth header to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  register: (data) => api.post("/auth/register", data),
  me: () => api.get("/auth/me"),
  changePassword: (currentPassword, newPassword) => 
    api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword }),
  getRelease: () => api.get("/auth/release"),
  markReleaseSeen: () => api.post("/auth/mark-release-seen"),
};

// Users
export const usersApi = {
  getAll: () => api.get("/users"),
  getEmployees: () => api.get("/users/employees"),
  getBillers: () => api.get("/users/billers"),
  getAllForFeedback: () => api.get("/users/all-for-feedback"),
  getCredentials: () => api.get("/users/credentials"),
  create: (data) => api.post("/users", data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  importBulk: (users) => api.post("/users/import-bulk", { users }),
  regeneratePassword: (id) => api.post(`/users/regenerate-password/${id}`),
  seedCelebrations: () => api.post("/users/seed-celebrations"),
};

// Celebrations (Birthdays & Anniversaries)
export const celebrationsApi = {
  get: () => api.get("/celebrations"),
};

// Stats
export const statsApi = {
  create: (data) => api.post("/stats", data),
  update: (id, data) => api.put(`/stats/${id}`, data),
  delete: (id) => api.delete(`/stats/${id}`),
  getByUser: (userId) => api.get(`/stats/user/${userId}`),
  getAll: () => api.get("/stats/all"),
  getYtd: (userId, year, month) => {
    let url = `/stats/ytd/${userId}`;
    const params = [];
    if (year) params.push(`year=${year}`);
    if (month) params.push(`month=${month}`);
    if (params.length) url += `?${params.join('&')}`;
    return api.get(url);
  },
  getFirmOverview: (year, month) => {
    let url = '/stats/firm-overview';
    const params = [];
    if (year) params.push(`year=${year}`);
    if (month) params.push(`month=${month}`);
    if (params.length) url += `?${params.join('&')}`;
    return api.get(url);
  },
};

// Benchmarks
export const benchmarksApi = {
  get: () => api.get("/benchmarks"),
  update: (data) => api.put("/benchmarks", data),
};

// Partner Billings
export const billingsApi = {
  create: (data) => api.post("/billings", data),
  update: (id, data) => api.put(`/billings/${id}`, data),
  delete: (id) => api.delete(`/billings/${id}`),
  getAll: () => api.get("/billings/all"),
  getByUser: (userId) => api.get(`/billings/user/${userId}`),
  getSummary: (year, month, maxMonth) => {
    let url = '/billings/summary';
    const params = [];
    if (year) params.push(`year=${year}`);
    if (month) params.push(`month=${month}`);
    if (maxMonth) params.push(`max_month=${maxMonth}`);
    if (params.length) url += `?${params.join('&')}`;
    return api.get(url);
  },
};

// AI Insights
export const aiApi = {
  getInsights: (year) => api.get(`/ai/insights${year ? `?year=${year}` : ''}`),
};

// Documents
export const documentsApi = {
  upload: (formData) => api.post("/documents", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  }),
  getAll: () => api.get("/documents"),
  download: (id) => api.get(`/documents/${id}/download`),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Feedback
export const feedbackApi = {
  create: (data) => api.post("/feedback", data),
  getAll: () => api.get("/feedback"),
  getReceived: () => api.get("/feedback/received"),
  getSent: () => api.get("/feedback/sent"),
  markRead: (id) => api.put(`/feedback/${id}/read`),
  getUnreadCount: () => api.get("/feedback/unread-count"),
};

// Firm Templates
export const templatesApi = {
  getAll: () => api.get("/templates"),
  getDefaults: () => api.get("/templates/defaults"),
  create: (data) => api.post("/templates", data),
  get: (id) => api.get(`/templates/${id}`),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
  generateLetter: (data) => api.post("/templates/generate", data),
  getLetters: () => api.get("/templates/letters"),
  getLetter: (id) => api.get(`/templates/letters/${id}`),
  updateLetter: (id, data) => api.put(`/templates/letters/${id}`, data),
  deleteLetter: (id) => api.delete(`/templates/letters/${id}`),
};

// Tax Calculator (2026)
export const taxCalculatorApi = {
  getConstants: () => api.get("/tax-calculator/constants"),
  getIrmaaBrackets: (filingStatus) => api.get(`/tax-calculator/irmaa-brackets/${filingStatus}`),
  calculate: (inputData) => api.post("/tax-calculator/calculate", inputData),
  createProjection: (data) => api.post("/tax-calculator/projections", data),
  getProjections: () => api.get("/tax-calculator/projections"),
  getProjection: (id) => api.get(`/tax-calculator/projections/${id}`),
  updateProjection: (id, data) => api.put(`/tax-calculator/projections/${id}`, data),
  deleteProjection: (id) => api.delete(`/tax-calculator/projections/${id}`),
};

// Digital Shelf (pull-based work queue)
export const queueApi = {
  getShelf: () => api.get("/queue/shelf"),
  getMine: () => api.get("/queue/mine"),
  getMyAdditions: () => api.get("/queue/my-additions"),
  claim: (id) => api.post(`/queue/${id}/claim`),
  release: (id, data) => api.post(`/queue/${id}/release`, data),
  setStatus: (id, data) => api.post(`/queue/${id}/status`, data),
  createItem: (data) => api.post("/queue/items", data),
  updateItem: (id, data) => api.put(`/queue/items/${id}`, data),
  deleteItem: (id) => api.delete(`/queue/items/${id}`),
  getBoard: () => api.get("/queue/board"),
  getMetrics: () => api.get("/queue/metrics"),
  getLeaderboard: (period) => api.get(`/queue/leaderboard?period=${period}`),
  getConfig: () => api.get("/queue/config"),
  sendDigestNow: () => api.post("/queue/digest/send-now"),
  previewDigest: (email) => api.post("/queue/digest/preview", { email: email || null }),
  saveConfig: (data) => api.put("/queue/config", data),
  getNotifications: () => api.get("/queue/notifications"),
  markNotificationsSeen: () => api.post("/queue/notifications/seen"),
};

export default api;
